import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { auth, db } from '../../lib/firebase';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { ensureReferralCode } from '../../services/referrals';

interface ReferralRow {
  userId: string;
  name?: string | null;
  email?: string | null;
  createdAt?: any;
}

const ReferralsPage: React.FC = () => {
  const navigate = useNavigate();
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [uid, setUid] = useState<string | null>(null);
  const [referralCode, setReferralCode] = useState<string>('');
  const [rows, setRows] = useState<ReferralRow[]>([]);
  const [copiedCode, setCopiedCode] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);

  useEffect(() => {
    const unsub = auth.onAuthStateChanged(async (user) => {
      if (!user) {
        setCheckingAuth(false);
        setUid(null);
        return;
      }
      setUid(user.uid);
      setCheckingAuth(false);
      try {
        const code = await ensureReferralCode(user.uid);
        setReferralCode(code);
      } catch {}
      try {
        const sub = collection(doc(db, 'users', user.uid), 'referrals');
        const snap = await getDocs(sub);
        const list = snap.docs.map(d => ({ id: d.id, ...(d.data() as any) }));
        // Enrich missing name/email from user profile docs
        const enriched = await Promise.all(list.map(async (r: any) => {
          if (!r.name || !r.email) {
            try {
              const uSnap = await getDoc(doc(db, 'users', r.userId));
              if (uSnap.exists()) {
                const u = uSnap.data() as any;
                return {
                  ...r,
                  name: r.name ?? (u.fullName || null),
                  email: r.email ?? (u.email || null),
                };
              }
            } catch {}
          }
          return r;
        }));
        // Sort newest first by createdAt if available
        enriched.sort((a: any, b: any) => {
          const ta = a.createdAt?.toMillis ? a.createdAt.toMillis() : (a.createdAt?.seconds ? a.createdAt.seconds * 1000 : 0);
          const tb = b.createdAt?.toMillis ? b.createdAt.toMillis() : (b.createdAt?.seconds ? b.createdAt.seconds * 1000 : 0);
          return tb - ta;
        });
        setRows(enriched);
      } catch {}
    });
    return () => unsub();
  }, []);

  const appUrl = (import.meta.env.VITE_APP_URL || window.location.origin);
  const shareLink = `${appUrl}/auth/sign-up?ref=${referralCode}`;

  const copy = async (text: string, setFlag: (v: boolean) => void) => {
    try {
      await navigator.clipboard.writeText(text);
      setFlag(true);
      setTimeout(() => setFlag(false), 1500);
    } catch {}
  };

  if (checkingAuth) return <div className="min-h-screen bg-white"></div>;
  if (!uid) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <main className="max-w-3xl mx-auto px-4 py-10">
          <div className="bg-white shadow rounded-lg p-6 text-center">
            <p className="text-gray-700 mb-4">Please sign in to view your referrals.</p>
            <button onClick={() => navigate('/auth/sign-in?redirect=/referrals')} className="px-4 py-2 bg-teal-600 text-white rounded">Sign In</button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="flex items-center gap-3 mb-6">
          <span className="text-3xl">🎁</span>
          <div>
            <h1 className="text-xl md:text-2xl font-bold">Referrals</h1>
            <p className="text-gray-600 text-sm">Invite friends and track who joined with your code.</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div className="bg-white shadow rounded-lg p-5">
            <h2 className="text-sm font-semibold text-gray-700 mb-2">Your Referral Code</h2>
            <div className="flex items-center justify-between border rounded px-4 py-3">
              <span className="font-mono text-lg">{referralCode || '...'}</span>
              <button onClick={() => copy(referralCode, setCopiedCode)} className="px-3 py-1 bg-teal-600 text-white rounded text-sm">{copiedCode ? 'Copied!' : 'Copy'}</button>
            </div>
            <p className="text-xs text-gray-500 mt-2">Share this code on signup.</p>
          </div>

          <div className="bg-white shadow rounded-lg p-5">
            <h2 className="text-sm font-semibold text-gray-700 mb-2">Shareable Link</h2>
            <div className="flex items-center justify-between border rounded px-4 py-3 overflow-x-auto">
              <span className="font-mono text-sm md:text-base whitespace-nowrap">{shareLink}</span>
              <button onClick={() => copy(shareLink, setCopiedLink)} className="px-3 py-1 bg-teal-600 text-white rounded text-sm">{copiedLink ? 'Copied!' : 'Copy Link'}</button>
            </div>
            <div className="flex flex-wrap items-center gap-2 mt-3">
              <a
                href={`https://wa.me/?text=${encodeURIComponent('Join me on Campus Market! ' + shareLink)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center space-x-2 px-3 py-1 rounded bg-green-50 text-green-700 hover:bg-green-100"
              >
                <i className="ri-whatsapp-line"></i>
                <span className="text-sm">WhatsApp</span>
              </a>
              <a
                href={`https://twitter.com/intent/tweet?text=${encodeURIComponent('Shop & sell on Campus Market!')}&url=${encodeURIComponent(shareLink)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center space-x-2 px-3 py-1 rounded bg-blue-50 text-blue-700 hover:bg-blue-100"
              >
                <i className="ri-twitter-line"></i>
                <span className="text-sm">Twitter</span>
              </a>
              <button
                onClick={() => copy(shareLink, setCopiedLink)}
                className="inline-flex items-center space-x-2 px-3 py-1 rounded bg-gray-100 text-gray-700 hover:bg-gray-200"
              >
                <i className="ri-link"></i>
                <span className="text-sm">Copy Link</span>
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-2">Anyone who signs up through this link will appear below.</p>
          </div>
        </div>

        <div className="bg-white shadow rounded-lg">
          <div className="p-5 border-b">
            <h2 className="text-sm font-semibold text-gray-700">Referred Users</h2>
          </div>
          <div className="p-5 overflow-x-auto">
            {rows.length === 0 ? (
              <div className="py-12 flex flex-col items-center justify-center text-center">
                <span className="text-4xl mb-3">🫶</span>
                <p className="text-gray-800 font-semibold">No referrals yet</p>
                <p className="text-gray-600 text-sm mt-1">Share your link to get started.</p>
                <button
                  onClick={() => copy(shareLink, setCopiedLink)}
                  className="mt-4 px-3 py-1 bg-teal-600 text-white rounded text-sm"
                >
                  {copiedLink ? 'Copied!' : 'Copy link'}
                </button>
              </div>
            ) : (
              <table className="min-w-full text-xs md:text-sm">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="text-left px-3 py-2">Name</th>
                    <th className="text-left px-3 py-2">Email</th>
                    <th className="text-left px-3 py-2">User ID</th>
                    <th className="text-left px-3 py-2">Joined</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.userId} className="border-b">
                      <td className="px-3 py-2">{r.name || '-'}</td>
                      <td className="px-3 py-2">{r.email || '-'}</td>
                      <td className="px-3 py-2 font-mono">{r.userId}</td>
                      <td className="px-3 py-2 text-gray-600">{r.createdAt ? new Date(r.createdAt.toMillis ? r.createdAt.toMillis() : r.createdAt).toLocaleString() : '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default ReferralsPage;